import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";

import {
  entities as entitiesApi,
  relationships as relApi,
  signals as signalsApi,
} from "@/lib/api";
import type { ApiEntity, ApiRelationship, ApiSignal } from "@/lib/api";
import { useTimeRange } from "@/lib/use-time-range";

/**
 * LiveRiskMatrixCanvas — likelihood × impact for each entity, computed live.
 *
 * **Likelihood (1-5):** density of recent signals (last 90 days) where this
 * entity appears in `related_entity_ids`. More recent activity → higher
 * likelihood of being in play.
 *
 * **Impact (1-5):** degree of connectivity (relationships in + out) blended
 * with a domain hint (e.g. armed_group strength). A node with many connections
 * has a bigger blast radius if compromised / activated.
 *
 * These are deliberately simple heuristics. Customers tune them per-ontology
 * as v1.2 risk scoring lands; for the showcase the goal is to show that the
 * matrix populates from real data, not from hard-coded mock values.
 */

const RECENT_DAYS = 90;

const TYPE_HEX: Record<string, string> = {
  armed_group: "#e55a3c",
  municipality: "#3b82f6",
  military_base: "#22b8d4",
  infrastructure: "#f59e0b",
  individual: "#22c55e",
  political_figure: "#a855f7",
  civilian_organization: "#eab308",
  default: "#9ca3af",
};

function colorFor(type: string): string {
  return TYPE_HEX[type] ?? TYPE_HEX.default;
}

const LIKELIHOOD_LABELS = ["Very Low", "Low", "Medium", "High", "Very High"];
const IMPACT_LABELS = ["Insignificant", "Minor", "Moderate", "Major", "Catastrophic"];

function bucket(value: number, thresholds: [number, number, number, number]): 1 | 2 | 3 | 4 | 5 {
  if (value <= thresholds[0]) return 1;
  if (value <= thresholds[1]) return 2;
  if (value <= thresholds[2]) return 3;
  if (value <= thresholds[3]) return 4;
  return 5;
}

interface Plotted {
  entity: ApiEntity;
  likelihood: 1 | 2 | 3 | 4 | 5;
  impact: 1 | 2 | 3 | 4 | 5;
  likelihoodRaw: number;
  impactRaw: number;
}

function compute(
  entities: ApiEntity[],
  rels: ApiRelationship[],
  signals: ApiSignal[],
  windowFromMs: number | null,
  windowToMs: number | null,
): Plotted[] {
  // When a window is active, that window is the likelihood denominator. When
  // not, fall back to the canvas's default RECENT_DAYS lookback.
  const effFrom =
    windowFromMs ?? Date.now() - RECENT_DAYS * 86400_000;
  const effTo = windowToMs ?? Date.now();

  // Pre-compute lookups
  const signalCount = new Map<string, number>();
  for (const s of signals) {
    const ts = new Date(s.timestamp).getTime();
    if (ts < effFrom || ts > effTo) continue;
    for (const eid of s.related_entity_ids ?? []) {
      signalCount.set(eid, (signalCount.get(eid) ?? 0) + 1);
    }
  }
  const relCount = new Map<string, number>();
  for (const r of rels) {
    relCount.set(r.source_entity_id, (relCount.get(r.source_entity_id) ?? 0) + 1);
    relCount.set(r.target_entity_id, (relCount.get(r.target_entity_id) ?? 0) + 1);
  }

  return entities.map((e) => {
    const sig = signalCount.get(e.id) ?? 0;
    const rel = relCount.get(e.id) ?? 0;
    // Domain hint: armed_group strength adds to impact (scale 0..1)
    let strength = 0;
    if (e.type === "armed_group" && typeof e.attributes?.strength === "number") {
      strength = Math.min(1, (e.attributes.strength as number) / 10000);
    }
    const impactRaw = rel + strength * 3;

    return {
      entity: e,
      likelihood: bucket(sig, [0, 1, 3, 6]),
      impact: bucket(impactRaw, [0, 1, 2.5, 4]),
      likelihoodRaw: sig,
      impactRaw,
    };
  });
}

// Heatmap cell color — risk goes up-right
function cellColor(x: number, y: number): string {
  const score = (x + y) / 2; // 1..5
  if (score >= 4.5) return "#7f1d1d"; // critical red
  if (score >= 3.5) return "#b91c1c";
  if (score >= 2.5) return "#d97706";
  if (score >= 1.5) return "#a16207";
  return "#166534";
}

export function LiveRiskMatrixCanvas({ workspaceId }: { workspaceId: string }) {
  const ents = useQuery({
    queryKey: ["entities", workspaceId],
    queryFn: () => entitiesApi.list(workspaceId),
    enabled: Boolean(workspaceId),
  });
  const rels = useQuery({
    queryKey: ["relationships", workspaceId],
    queryFn: () => relApi.list(workspaceId),
    enabled: Boolean(workspaceId),
  });
  const sigs = useQuery({
    queryKey: ["signals", workspaceId],
    queryFn: () => signalsApi.list(workspaceId),
    enabled: Boolean(workspaceId),
  });

  const [hover, setHover] = useState<Plotted | null>(null);
  const tr = useTimeRange();

  const plotted = useMemo(
    () => compute(ents.data ?? [], rels.data ?? [], sigs.data ?? [], tr.fromMs, tr.toMs),
    [ents.data, rels.data, sigs.data, tr.fromMs, tr.toMs],
  );

  // Group entities falling in the same cell
  const byCell = useMemo(() => {
    const m = new Map<string, Plotted[]>();
    for (const p of plotted) {
      const key = `${p.likelihood}-${p.impact}`;
      const arr = m.get(key) ?? [];
      arr.push(p);
      m.set(key, arr);
    }
    return m;
  }, [plotted]);

  return (
    <div className="relative h-full w-full overflow-hidden rounded-md bg-[oklch(0.14_0.01_260)] flex flex-col">
      <div className="flex items-center justify-between px-3 py-2 border-b border-border bg-surface/60 text-[11px]">
        <div className="flex items-center gap-4 text-foreground/80">
          <span>
            <span className="font-mono text-foreground">{plotted.length}</span>{" "}
            <span className="text-muted-foreground">entities scored</span>
          </span>
          <span className="text-muted-foreground">
            likelihood = signals in window
            {tr.isActive
              ? ` (${tr.from ?? "−∞"} → ${tr.to ?? "now"})`
              : ` (last ${RECENT_DAYS}d)`}{" "}
            · impact = connectivity + domain hints
          </span>
        </div>
      </div>

      <div className="flex-1 min-h-0 p-6 overflow-auto">
        <div className="grid grid-cols-[80px_minmax(0,1fr)] gap-3 h-full">
          {/* Y axis (impact) */}
          <div className="flex flex-col-reverse text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
            {IMPACT_LABELS.map((label, i) => (
              <div
                key={label}
                className="flex-1 flex items-center justify-end gap-1.5 pr-1.5"
                style={{ minHeight: 70 }}
              >
                <span className="font-mono text-foreground/85">{i + 1}</span>
                <span className="text-right max-w-[60px] leading-tight">{label}</span>
              </div>
            ))}
          </div>

          {/* Grid + bubbles */}
          <div className="flex flex-col">
            <div
              className="relative grid grid-cols-5 grid-rows-5 gap-1"
              style={{ aspectRatio: "1.2 / 1", minHeight: 360 }}
            >
              {Array.from({ length: 25 }).map((_, i) => {
                const x = (i % 5) + 1;
                const y = 5 - Math.floor(i / 5);
                return (
                  <div
                    key={i}
                    className="rounded"
                    style={{ background: cellColor(x, y), opacity: 0.5 }}
                  />
                );
              })}

              {/* Bubble overlay */}
              {[...byCell.entries()].map(([key, group]) => {
                const [lik, imp] = key.split("-").map(Number) as [1 | 2 | 3 | 4 | 5, 1 | 2 | 3 | 4 | 5];
                // Position in % across the 5x5 grid
                const xPct = ((lik - 1) / 5) * 100 + 10;
                const yPct = 100 - (((imp - 1) / 5) * 100 + 10);
                return (
                  <div
                    key={key}
                    className="absolute pointer-events-none"
                    style={{
                      left: `${xPct}%`,
                      top: `${yPct}%`,
                      transform: "translate(-50%, -50%)",
                    }}
                  >
                    <BubbleStack group={group} onHover={setHover} />
                  </div>
                );
              })}
            </div>

            {/* X axis (likelihood) */}
            <div className="mt-3 grid grid-cols-5 gap-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground text-center">
              {LIKELIHOOD_LABELS.map((label, i) => (
                <div key={label}>
                  <div className="font-mono text-foreground/85 mb-0.5">{i + 1}</div>
                  <div className="leading-tight">{label}</div>
                </div>
              ))}
            </div>
            <div className="text-center text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mt-3">
              Likelihood →
            </div>
          </div>
        </div>
      </div>

      {/* Hover detail */}
      {hover && (
        <div className="absolute left-3 bottom-3 z-30 rounded-md border border-border bg-popover px-3 py-2.5 text-[11px] shadow-xl max-w-xs">
          <div className="font-semibold text-foreground mb-1">{hover.entity.name}</div>
          <div className="text-muted-foreground text-[10px] mb-2">{hover.entity.type}</div>
          <div className="grid grid-cols-2 gap-2 text-[10px]">
            <div>
              <div className="text-muted-foreground">Likelihood</div>
              <div className="text-foreground/90">
                {hover.likelihood} ({LIKELIHOOD_LABELS[hover.likelihood - 1]})
              </div>
              <div className="text-muted-foreground text-[9px]">
                {hover.likelihoodRaw} signal{hover.likelihoodRaw === 1 ? "" : "s"} in {RECENT_DAYS}d
              </div>
            </div>
            <div>
              <div className="text-muted-foreground">Impact</div>
              <div className="text-foreground/90">
                {hover.impact} ({IMPACT_LABELS[hover.impact - 1]})
              </div>
              <div className="text-muted-foreground text-[9px]">
                connectivity {hover.impactRaw.toFixed(1)}
              </div>
            </div>
          </div>
        </div>
      )}

      {plotted.length === 0 && (
        <div className="absolute inset-0 grid place-items-center text-center px-6 pointer-events-none">
          <div className="max-w-sm rounded-md border border-border bg-surface/95 p-4 pointer-events-auto">
            <div className="label-eyebrow mb-2">Nothing to score</div>
            <p className="text-[12px] text-muted-foreground">
              Add entities, relationships, and recent signals via chat. The
              matrix scores live from the graph.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

function BubbleStack({
  group,
  onHover,
}: {
  group: Plotted[];
  onHover: (p: Plotted | null) => void;
}) {
  // For multiple entities in the same cell, stack labels vertically.
  const primary = group[0];
  const color = colorFor(primary.entity.type);
  const size = 28 + Math.min(20, group.length * 4);

  return (
    <div
      className="relative flex flex-col items-center pointer-events-auto"
      onMouseLeave={() => onHover(null)}
    >
      <div
        className="grid place-items-center rounded-full border-2 cursor-pointer transition"
        style={{
          width: size,
          height: size,
          background: `color-mix(in oklab, ${color} 30%, transparent)`,
          borderColor: color,
          boxShadow: `0 0 16px color-mix(in oklab, ${color} 45%, transparent)`,
        }}
        onMouseEnter={() => onHover(primary)}
      >
        <span className="text-[10px] font-bold text-foreground">
          {group.length > 1 ? group.length : ""}
        </span>
      </div>
      <div className="absolute top-full mt-1 flex flex-col items-center pointer-events-none">
        {group.slice(0, 3).map((p) => (
          <div
            key={p.entity.id}
            className="text-[9px] font-semibold leading-tight text-foreground/95 whitespace-nowrap"
            style={{ textShadow: "0 1px 2px rgba(0,0,0,0.9)" }}
          >
            {p.entity.name}
          </div>
        ))}
        {group.length > 3 && (
          <div className="text-[8px] text-muted-foreground">+{group.length - 3}</div>
        )}
      </div>
    </div>
  );
}
